import React, { useRef, useState, useCallback, useEffect } from "react";
import { CaretRight, CaretLeft } from "@phosphor-icons/react";

interface ScrollableCardsProps {
  children: React.ReactNode;
  className?: string;
}

export function ScrollableCards({ children, className }: ScrollableCardsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const cards = Array.from(el.children) as HTMLElement[];
    const containerRect = el.getBoundingClientRect();

    if (direction === "right") {
      const next = cards.find((card) => {
        const rect = card.getBoundingClientRect();
        return rect.right > containerRect.right;
      });
      if (next) {
        const cardCenter = next.offsetLeft + next.offsetWidth / 2;
        el.scrollTo({ left: cardCenter - el.clientWidth / 2 + 15, behavior: "smooth" });
      }
    } else {
      const prev = [...cards].reverse().find((card) => {
        const rect = card.getBoundingClientRect();
        return rect.left < containerRect.left;
      });
      if (prev) {
        const cardCenter = prev.offsetLeft + prev.offsetWidth / 2;
        el.scrollTo({ left: cardCenter - el.clientWidth / 2 - 15, behavior: "smooth" });
      }
    }
  };

  return (
    <div className="lk-scrollable-cards">
      <div ref={scrollRef} className={className}>
        {children}
      </div>
      {canScrollLeft && (
        <button
          className="lk-scroll-arrow lk-scroll-arrow--left"
          onClick={() => scroll("left")}
          aria-label="Scroll left"
        >
          <CaretLeft size={20} weight="bold" />
        </button>
      )}
      {canScrollRight && (
        <button
          className="lk-scroll-arrow lk-scroll-arrow--right"
          onClick={() => scroll("right")}
          aria-label="Scroll right"
        >
          <CaretRight size={20} weight="bold" />
        </button>
      )}
    </div>
  );
}
